import { HOSTED_CONTACT_EMAIL, PRICING } from '../data/content';
import type { PricingCard } from '../data/content';
import { useReveal } from '../hooks/useReveal';
import './Pricing.scss';

/**
 * Pricing section. Cards are *derived* from `@pageloop/shared` PLAN_CATALOG
 * (via `PRICING` in `../data/content`) so id / name / price always match
 * what Stripe charges and what the signup flow accepts.
 *
 * Layout is intentionally not a flat 5-up grid — it's three bands so the
 * page reads less like a cookie-cutter pricing table:
 *   - Free  → a wide, open banner on its own row above the paid tiers.
 *   - Indie / Pro / Team → the three-card row (Pro sits forward).
 *   - Enterprise → a wide, open banner on its own row below.
 * CTAs route to {signup}?plan=<id>; Enterprise routes to a mailto.
 */
export function Pricing() {
	const ref = useReveal<HTMLDivElement>();
	const free = PRICING.find((p) => p.tier === 'free');
	const enterprise = PRICING.find((p) => p.tier === 'enterprise');
	const tiers = PRICING.filter((p) => p.tier === 'indie' || p.tier === 'pro' || p.tier === 'team');

	return (
		<section class="m-pricing" id="pricing">
			<div class="m-pricing__inner" ref={ref}>
				<header class="m-pricing__head">
					<span class="m-pricing__eyebrow">pricing</span>
					<h2 class="m-pricing__title">simple, by design.</h2>
					<p class="m-pricing__sub">
						Start free — for a small team or a solo builder. Step up as you grow.
						Annual plans save about 17% (two months free), and every tier includes
						unlimited anonymous commenters: the people leaving feedback are never
						the meter you pay against.
					</p>
				</header>

				{free && <PlanBanner plan={free} variant="free" />}

				<div class="m-pricing__row">
					{tiers.map((plan) => (
						<article
							key={plan.tier}
							class={`m-pricing__card ${plan.popular ? 'm-pricing__card--popular' : ''}`}
						>
							{plan.popular && <span class="m-pricing__badge">most popular</span>}
							<header class="m-pricing__card-head">
								<span class="m-pricing__card-name">{plan.name}</span>
								<span class="m-pricing__card-tag">{plan.tagline}</span>
							</header>
							<div class="m-pricing__card-price">
								<span class="m-pricing__card-amount">{plan.price}</span>
								<span class="m-pricing__card-period">{plan.period}</span>
							</div>
							{plan.annualNote && (
								<span class="m-pricing__card-annual">{plan.annualNote}</span>
							)}
							<ul class="m-pricing__card-list">
								{plan.features.map((feat) => (
									<li key={feat} class="m-pricing__card-item">
										<span class="m-pricing__card-check" aria-hidden="true" />
										<span>{feat}</span>
									</li>
								))}
							</ul>
							<a
								class={`m-btn ${plan.popular ? 'm-btn--primary' : 'm-btn--ghost'} m-pricing__card-cta`}
								href={plan.ctaHref}
							>
								{plan.cta}
							</a>
						</article>
					))}
				</div>

				{enterprise && <PlanBanner plan={enterprise} variant="enterprise" />}
			</div>
		</section>
	);
}

/**
 * Wide, open banner used for the Free (top) and Enterprise (bottom)
 * rows. Horizontal layout — identity + price on the left, a relaxed
 * feature list in the middle, the CTA on the right — so these two read
 * as friendly bookends rather than another column in the grid. Still a
 * single self-contained, independently-selectable card.
 */
function PlanBanner({ plan, variant }: { plan: PricingCard; variant: 'free' | 'enterprise' }) {
	const ref = useReveal<HTMLDivElement>();
	return (
		<article class={`m-pricing__banner m-pricing__banner--${variant}`} ref={ref}>
			<div class="m-pricing__banner-lead">
				<span class="m-pricing__banner-name">{plan.name}</span>
				<p class="m-pricing__banner-tag">{plan.tagline}</p>
				<div class="m-pricing__banner-price">
					<span class="m-pricing__banner-amount">{plan.price}</span>
					{plan.period && <span class="m-pricing__banner-period">{plan.period}</span>}
				</div>
				{plan.annualNote && (
					<span class="m-pricing__banner-annual">{plan.annualNote}</span>
				)}
			</div>

			<ul class="m-pricing__banner-list">
				{plan.features.map((feat) => (
					<li key={feat} class="m-pricing__banner-item">
						<span class="m-pricing__card-check" aria-hidden="true" />
						<span>{feat}</span>
					</li>
				))}
			</ul>

			<div class="m-pricing__banner-cta-wrap">
				<a
					class={`m-btn ${variant === 'free' ? 'm-btn--primary' : 'm-btn--ghost'} m-pricing__banner-cta`}
					href={plan.ctaHref}
				>
					{plan.cta}
				</a>
				{variant === 'enterprise' && (
					<a
						class="m-pricing__foot-link"
						href={`mailto:${HOSTED_CONTACT_EMAIL}?subject=Enterprise%20inquiry`}
					>
						or email us →
					</a>
				)}
			</div>
		</article>
	);
}
